import React, { type ComponentType, Fragment, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import { Box } from '@atlaskit/primitives/compiled';
import { WidthObserver } from '@atlaskit/width-detector/width-observer';
import {
	CornerStyle,
	ExtensionMode,
	HubExtensionsWrapper,
	NoExtensionData,
	type ProductDependenciesProviderProps,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import { useHubAutoTextColor } from '@atlassian/company-hub-common/hub-auto-text-color';
import { ELEMENT_DEFAULTS, resolveAdfColor } from '@atlassian/company-hub-common/hub-brand-palette';
import { resolveCornerRadiusOverride } from '@atlassian/company-hub-common/hub-corner-radius';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { ImageRenderer } from '@atlassian/company-hub-common/media-components';
import { SpotlightContent } from '@atlassian/company-hub-common/spotlight-content';
import {
	SpotlightLayout,
	VERTICAL_MAX_WIDTH,
} from '@atlassian/company-hub-common/spotlight-layout';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';

import type { SpotlightParameters } from '../../../../types';

type ImageSource = SpotlightParameters['content']['image']['imageSrc'];

const isValidSpotlightContent = (
	parameters: SpotlightParameters | undefined,
): parameters is SpotlightParameters => {
	const content = parameters?.content;
	return Boolean(content?.text && content.button && content.image);
};

const getImage = (src: ImageSource, contentId: string, alt?: string) => {
	return src ? (
		<ImageRenderer
			contentId={contentId}
			imageSrc={src}
			imageAlt={alt}
			shouldShowExternalSiteImage
		/>
	) : null;
};

const styles = cssMap({
	containerStyles: {
		position: 'relative',
		overflow: 'hidden',
	},
	previewStyles: { pointerEvents: 'none' },
});

export type SpotlightContainerProps = {
	contentId?: string;
	extensionNode?: ExtensionParams<SpotlightParameters>;
	isInEditor?: boolean;
	isPreviewSize?: boolean;
	PropsProvider?: ComponentType<ProductDependenciesProviderProps>;
};

export const SpotlightBase = ({
	extensionNode,
	contentId = '',
	isPreviewSize,
	isInEditor = false,
}: Omit<SpotlightContainerProps, 'PropsProvider'>): React.JSX.Element | null => {
	const { experienceStartComponent, isCompanyHub } = useProductDependencies();
	const ExperienceStart = experienceStartComponent || Fragment;
	const [width, setWidth] = useState(VERTICAL_MAX_WIDTH + 1);
	// useHubTheming returns undefined in cross-host trees that don't mount
	// <HubThemingProvider> (Confluence Company Hub); treat as gate-OFF so we
	// take the legacy CSS-var passthrough path.
	const theming = useHubTheming();
	const isAdvancedThemingEnabled = theming?.isAdvancedThemingEnabled ?? false;
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	const { parameters } = extensionNode || {};

	// Keep black/white text legible against the live brand colour. Called above
	// the early return so the hook order is stable, and reads the raw ADF values
	// because the resolved background below is a CSS var the colour helpers
	// cannot measure. An explicit brand-shade or custom-hex choice is returned
	// untouched. See `hub-auto-text-color`.
	const resolveTextColor = useHubAutoTextColor('spotlight-extension');
	const autoTextColor = resolveTextColor({
		backgroundColor: parameters?.backgroundColor,
		textColor: parameters?.content?.text?.textColor,
		defaultRef: ELEMENT_DEFAULTS['spotlight.bg'],
	});

	if (!isValidSpotlightContent(parameters)) {
		return isInEditor ? <NoExtensionData source="spotlight" /> : null;
	}

	const {
		backgroundColor: backgroundColorAdf,
		content,
		height,
		layout,
		cornerStyle,
		cornerRadius,
	} = parameters;
	// Advanced theming v2 — resolve the ADF value through the brand palette
	// (PaletteRef / { custom: hex } / legacy `var(--hub-*)` / raw hex).
	// When the gate is OFF, pass the raw ADF value through unchanged so the
	// browser still resolves the legacy `var(--hub-primary-color, ...)` strings
	// against `setHubBrandingStyle`'s writes — matches pre-v2 behaviour exactly.
	//
	// `resolveAdfColor` returns a `var(--hub-color-primary-200)` reference for
	// tracked values and a raw hex for custom overrides. The CSS cascade
	// resolves the var ref against `<HubThemingProvider>`'s wrapper div on
	// first paint — no re-render needed when the brand color changes.
	const backgroundColor = isAdvancedThemingEnabled
		? resolveAdfColor(backgroundColorAdf, ELEMENT_DEFAULTS['spotlight.bg'])
		: backgroundColorAdf;
	const {
		text: { title, description, textColor },
		button: { isButtonShown, text: buttonText, link, style: buttonStyle },
	} = content;

	// Resolve per-extension override: numeric value takes precedence, else map legacy enum
	const resolvedCornerRadiusOverride = isCornerRadiusEnabled
		? resolveCornerRadiusOverride(cornerRadius, cornerStyle)
		: undefined;
	// Gate ON: derive rounded/squared from the EFFECTIVE corner radius (the local
	// override, else the inherited hub global) so a 0 radius renders truly square —
	// this drives the edit-mode squared-corner inset below, matching how the legacy
	// Square option looked. Gate OFF: legacy behaviour (customized corner style is
	// only offered on Company Hub, otherwise it defaults to round).
	const useRoundedCorners = isCornerRadiusEnabled
		? (resolvedCornerRadiusOverride ?? theming?.cornerRadius) !== 0
		: cornerStyle === CornerStyle.ROUNDED || !isCompanyHub;

	return (
		<HubExtensionsWrapper<SpotlightParameters>
			mode={ExtensionMode.RENDERER}
			extensionNode={extensionNode}
			contentId={contentId}
			showNoDataState={!parameters}
			packageName="spotlight-extension"
		>
			<ExperienceStart />
			<Box
				xcss={cx(styles.containerStyles, isPreviewSize && styles.previewStyles)}
				// tiny bit of padding here in edit mode helps user to see their
				// squared corner style, since the extension container border radius
				// cuts it off
				paddingBlock={isInEditor && !useRoundedCorners ? 'space.050' : undefined}
				testId={`spotlight-${useRoundedCorners ? 'rounded' : 'squared'}`}
			>
				{!isPreviewSize && <WidthObserver setWidth={setWidth} offscreen />}
				<SpotlightLayout
					testId="spotlight-extension"
					backgroundColor={backgroundColor}
					height={height}
					layout={layout}
					image={getImage(content.image.imageSrc, contentId, content.image.altText)}
					width={width}
					hasRoundedCorners={useRoundedCorners}
					cornerRadiusOverride={resolvedCornerRadiusOverride}
				>
					<SpotlightContent
						layout={layout}
						title={title}
						description={description}
						textColor={autoTextColor ?? textColor}
						isButtonShown={isButtonShown}
						buttonText={buttonText}
						link={link}
						buttonStyle={buttonStyle}
						width={width}
						source="spotlightExtension"
					/>
				</SpotlightLayout>
			</Box>
		</HubExtensionsWrapper>
	);
};

const SpotlightWithProvider = (props: SpotlightContainerProps): React.JSX.Element | null => {
	const { extensionNode, contentId = '', isInEditor, PropsProvider = Fragment } = props;

	return (
		<PropsProvider
			mode={isInEditor ? ExtensionMode.EDITOR : ExtensionMode.RENDERER}
			contentId={contentId}
			extensionNode={extensionNode}
		>
			<SpotlightBase {...props} />
		</PropsProvider>
	);
};

export const Spotlight: (props: SpotlightContainerProps) => React.JSX.Element | null =
	SpotlightWithProvider;
